import { defineComponent as _defineComponent } from 'vue'
import * as yup from "yup";
import { toRefs, ref, computed, watch, onUnmounted, useContext } from "@nuxtjs/composition-api";
import { useMobile } from "~/core/composables/useMobile";
import { InfoText } from "~/core/types/purchase-popup-2/InfoText";
import { MetaUser } from "~/core/models/MetaUser";
import MunicipalityTx from "~/core/models/purchase-popup-2/MunicipalityTx";
import ERROR_MESSAGES from "~/core/validators/error-messages";
import AuthService from "~/core/services/api-interaction/AuthService";
import useHiddenWalletAddress from "~/core/composables/useHiddenWalletAddress";

interface Props {
    initialProductOwner?: (typeof OWNERS)[keyof typeof OWNERS];
    user: MetaUser,
    canBuyFor: any,
    transactionObject: MunicipalityTx,
    infoTexts: InfoText[],
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductOwnerSelection',
  props: {
    initialProductOwner: { default: '' },
    user: null,
    canBuyFor: null,
    transactionObject: null,
    infoTexts: { default: () => [] }
  },
  emits: ['onStartLoading', 'onStopLoading', 'onProductOwnerSelected'],
  setup(__props: any, { emit: $emit }) {

const props = __props as { initialProductOwner: (typeof OWNERS)[keyof typeof OWNERS], user: MetaUser, canBuyFor: any, transactionObject: MunicipalityTx, infoTexts: InfoText[] };

const OWNERS = {
    CURRENT_USER: 'me',
    ANOTHER_USER: 'another_user',
}





const {
    initialProductOwner,
    user,
    canBuyFor,
    transactionObject,
    infoTexts,
} = toRefs(props);

const context = useContext();
const isMobile = useMobile();
const productOwner = ref(initialProductOwner.value || '');
const isEmailValid = ref(false);
const emailError = ref('');
const ownerWalletAddress = ref(null);
const selectedEmail = ref(null);
const isWalletAddressLoading = ref(false);
const permissionTimeout = ref(null);
const disableForOthers = ref(true);
const disableForMe = ref(true);

const schema = yup.object().shape({
    selectedEmail: yup
        .string()
        .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            'please_enter_a_valid_email_address'
        )
        .required('please_enter_your_email_address'),
});

const productOwnerOptions = computed(() => [
    {
        title: context.i18n.t('Purchase for myself'),
        value: OWNERS.CURRENT_USER,
        disabled: disableForMe.value,
    },
    {
        title: context.i18n.t('Purchase for another user'),
        value: OWNERS.ANOTHER_USER,
        disabled: disableForOthers.value,
    },
]);
const giftUserInfo = computed(() => {
    if (ownerWalletAddress.value) {
        const hiddenAddress = useHiddenWalletAddress(ownerWalletAddress.value);
        return context.i18n.t('purchase_gift_for_user_wallet', { wallet: hiddenAddress.value });
    }
    return '';
});
const showInfoText = computed(() => {
    return transactionObject.value.method !== 'web3PurchaseCardBundle' && transactionObject.value.method !== 'purchaseSingleProduct';
})

const setupInitialBuyPermission = async () => {
    if (canBuyFor.value) {
        $emit('onStartLoading');
        try {
            const promises = [
                canBuyFor.value(false),
                canBuyFor.value(true),
            ];
            const [canBuyForOthers, canBuyForMe] = await Promise.all(promises);
            disableForOthers.value = !canBuyForOthers;
            disableForMe.value = !canBuyForMe;
            runBuyPermissionInterval();
            $emit('onStopLoading');
        } catch (e) {
            $emit('onStopLoading', e);
            disableForOthers.value = true;
            disableForMe.value = true;
        }
    } else {
        disableForOthers.value = false;
        disableForMe.value = false;
    }
}

const runBuyPermissionInterval = async () => {
    permissionTimeout.value = setTimeout(async () => {
        try {
            disableForOthers.value = !await canBuyFor.value(false);
        } catch (e) {
            disableForOthers.value = true;
        }
        if (ownerWalletAddress.value) {
            await checkBuyPermission(ownerWalletAddress.value, (val) => {
                emailError.value = val;
            });
        }
        try {
            disableForMe.value = !await canBuyFor.value(true);
        } catch (e) {
            disableForMe.value = true;
        }
        runBuyPermissionInterval();
    }, 10000);
}

const checkBuyPermission = async (address: string, callback: Function) => {
    try {
        const result = await canBuyFor.value(address);
        if (result) {
            callback();
            ownerWalletAddress.value = address || '';
        } else {
            ownerWalletAddress.value = '';
            callback(ERROR_MESSAGES.PURCHASE_PERMISSIONS.CANNOT_PURCHASE);
        }
    } catch(e) {
        ownerWalletAddress.value = '';
        callback(e.message);
    }
}

const getOwnerAddress = async (value: string) => {
    return await AuthService.getAddressByEmail(value);
}

const validateAndGetAddress = async () => {
    isEmailValid.value = true;
    emailError.value = '';

    const address = await getOwnerAddress(selectedEmail.value);

    if (canBuyFor.value && address) {
        await checkBuyPermission(address, (val) => {
            if (val) {
                throw Error(val);
            }
        });
    } else {
        ownerWalletAddress.value = address || '';
    }
};

const validateField = async () => {
    try {
        await schema.validateAt('selectedEmail', {
            selectedEmail: selectedEmail.value,
        });

        if (selectedEmail.value === user.value.email) {
            throw Error('cannot_purchase_gift_for_yourself');
        }

        await validateAndGetAddress();

    } catch (error) {
        isEmailValid.value = false;
        emailError.value = error.message;
        ownerWalletAddress.value = '';
    } finally {
        isWalletAddressLoading.value = false;
    }
};

setupInitialBuyPermission();

const constructProductOwnerAndEmit = async () => {
    $emit('onStartLoading');
    try {
        let walletAddress = user.value.walletAddress || user.value.relatedWalletAddress;
        if (productOwner.value === OWNERS.ANOTHER_USER) {
            walletAddress = ownerWalletAddress.value;
        }
        transactionObject.value.switchOwner(walletAddress);
        await transactionObject.value.updatePriceInfo();
        await transactionObject.value.estimateGas();
        $emit('onProductOwnerSelected');
        $emit('onStopLoading');
    } catch (e) {
        $emit('onStopLoading', e);
    }
}

watch(disableForMe, (val) => {
    if (val) {
        if (disableForOthers.value) {
            productOwner.value = '';
        } else {
            productOwner.value = OWNERS.ANOTHER_USER;
        }
    } else if (productOwner.value === '') {
        productOwner.value = OWNERS.CURRENT_USER;
    }
}, { immediate: true });

watch(disableForOthers, (val) => {
    if (val) {
        if (disableForMe.value) {
            productOwner.value = '';
        } else {
            productOwner.value = OWNERS.CURRENT_USER;
        }
    } else if (productOwner.value === '') {
        productOwner.value = OWNERS.ANOTHER_USER;
    }
}, { immediate: true });

let timeoutId;
watch(selectedEmail, (val) => {
    isWalletAddressLoading.value = true;
    isEmailValid.value = true;
    emailError.value = '';

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
        validateField();
    }, 500);
});

onUnmounted(() => {
    clearTimeout(permissionTimeout.value);
});

return { __sfc: true,OWNERS, $emit, props, initialProductOwner, user, canBuyFor, transactionObject, infoTexts, context, isMobile, productOwner, isEmailValid, emailError, ownerWalletAddress, selectedEmail, isWalletAddressLoading, permissionTimeout, disableForOthers, disableForMe, schema, productOwnerOptions, giftUserInfo, showInfoText, setupInitialBuyPermission, runBuyPermissionInterval, checkBuyPermission, getOwnerAddress, validateAndGetAddress, validateField, constructProductOwnerAndEmit, timeoutId }
}

})